import React, { useState, useEffect } from 'react';
import { Card, List, Avatar, Form, Input, Button, message, Space, Divider } from 'antd';
import { UserOutlined, MessageOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;

const FeedbackBoard: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // 获取所有反馈
  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/feedback');
      setFeedbacks(response.data.reverse()); // 最新的在前
    } catch (error) {
      message.error('Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  // 提交反馈
  const handleSubmitFeedback = async (values: any) => {
    setSubmitting(true);
    try {
      await axios.post('/api/feedback', {
        name: values.name || 'Anonymous',
        email: values.email || '',
        feedback: values.feedback,
        rating: 5
      });
      message.success('Feedback submitted successfully!');
      form.resetFields();
      fetchFeedbacks(); // 刷新列表
    } catch (error) {
      message.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f2f5',
      padding: '20px'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto'
      }}>
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <MessageOutlined style={{ marginRight: '8px' }} />
                Feedback Board
              </div>
              <Button 
                icon={<ReloadOutlined />}
                onClick={fetchFeedbacks}
                loading={loading}
              >
                Refresh
              </Button>
            </div>
          }
          style={{ marginBottom: '20px' }}
        >
          <List
            loading={loading}
            dataSource={feedbacks}
            locale={{ emptyText: 'No feedback yet' }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} feedbacks`
            }}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    <Space>
                      <span>{item.name || 'Anonymous'}</span>
                      {item.email && (
                        <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
                          ({item.email})
                        </span>
                      )}
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
                        {item.feedback}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {new Date(item.created_at).toLocaleString()}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        <Card title="Add New Feedback">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmitFeedback}
          >
            <Form.Item
              name="name"
              label="Name (Optional)"
            >
              <Input placeholder="Your name" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email (Optional)"
              rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
            >
              <Input placeholder="your.email@example.com" />
            </Form.Item>
            <Form.Item
              name="feedback"
              label="Your Feedback"
              rules={[{ required: true, message: 'Please share your feedback' }]}
            >
              <TextArea 
                rows={6} 
                placeholder="Please share your thoughts, suggestions, or report any issues..."
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting}
                block
                size="large"
                icon={<MessageOutlined />}
              >
                Submit Feedback
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default FeedbackBoard;

